import { useTranslator } from 'tradera/lang/translate';
import { ITEM_STATES } from 'tradera/state/views/view-item/constants';
import { AnimateOnValueChange } from './animate-on-value-change';
import LocalizedPrice from 'tradera/localization/localized-price';
import { Button } from 'tradera/components/atoms/button/button';
import type { PaymentCalculations } from 'tradera/state/services/types/webapi-browse-generated';
import { MandatoryBuyerProtectionInfo } from 'tradera/apps/tradera/components/vip/bidding/mandatory-buyer-protection-info';
import type { BuyerProtectionModalType } from 'tradera/apps/tradera/components/vip/seller-details/buyer-protection-info-modal';
import { classNames } from 'tradera/utils/class-names';

type BidDetailsProps = {
    bidCount?: number | null;
    itemState: ITEM_STATES | null;
    leadingBidAmount?: number | null;
    onShowBids: () => void;
    setBuyerProtectionModal: (type: BuyerProtectionModalType) => void;
    openingBid?: number | null;
    finalPrice?: number | null;
    paymentCalculations: PaymentCalculations;
    reservePriceReached?: boolean;
};

export const BidDetails = ({
    bidCount,
    itemState,
    leadingBidAmount,
    onShowBids,
    setBuyerProtectionModal,
    openingBid,
    finalPrice,
    paymentCalculations,
    reservePriceReached
}: BidDetailsProps) => {
    const { t } = useTranslator();
    const itemIsActive =
        itemState === ITEM_STATES.ONGOING_AUCTION_BIN ||
        itemState === ITEM_STATES.ONGOING_AUCTION;

    const hasBids = !!bidCount && bidCount > 0;
    const amountDetails = getAmountDetails(
        itemState,
        leadingBidAmount,
        openingBid,
        hasBids,
        finalPrice,
        paymentCalculations.paymentAmountForBid || 0,
        paymentCalculations.paymentAmountForFixedPrice || 0
    );

    const amountTitle = (
        <p className="text-nowrap mb-0 bid-details-amount-title">
            {t(amountDetails.title)}
        </p>
    );

    const amountValue = (
        <p
            className={classNames(
                itemIsActive && 'branded-underline mb-2',
                'text-nowrap heading-berlin  bid-details-amount mb-0'
            )}>
            <AnimateOnValueChange trigger={bidCount}>
                <LocalizedPrice price={amountDetails.amount} />
            </AnimateOnValueChange>
        </p>
    );

    const bidValue = (
        <p className="text-nowrap d-flex-column mb-0 ml-1 bid-details-bid-value">
            ∙
            {hasBids ? (
                <Button
                    theme="link"
                    className="m-n2 p-1 pl-3 bid-details-button text-lowercase text-gray-600 " // for larger clickable area
                    onClick={(e) => {
                        e?.preventDefault();
                        onShowBids();
                    }}>
                    {bidCount} {t('vip_bid')}
                </Button>
            ) : (
                <span className="ml-1 text-lowercase">
                    {bidCount} {t('vip_bid')}
                </span>
            )}
        </p>
    );

    const showBidCount =
        itemState !== ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN;

    return (
        <>
            <section
                className={classNames(
                    'bid-details pt-1',
                    itemIsActive && reservePriceReached ? 'pb-3' : 'pb-2'
                )}>
                <div className="d-flex flex-column align-items-baseline justify-content-between">
                    <div className="d-flex bid-details-bid-value font-hansen">
                        {amountTitle}
                        {showBidCount && bidValue}
                    </div>
                    <div className="mt-1">{amountValue}</div>
                </div>
                {paymentCalculations.isBuyerProtectionMandatory && (
                    <MandatoryBuyerProtectionInfo
                        setBuyerProtectionModal={setBuyerProtectionModal}
                        amountIncludingBuyerProtection={
                            amountDetails.amountIncludingBuyerProtection
                        }
                        className={classNames(itemIsActive ? 'mt-0' : 'mt-1')}
                    />
                )}
            </section>
        </>
    );
};

export const getAmountDetails = (
    itemState: ITEM_STATES | null,
    leadingBid?: number | null,
    openingBid?: number | null,
    hasBids?: boolean,
    finalPrice?: number | null,
    paymentAmountForBid?: number | null,
    paymentAmountForFixedPrice?: number | null
) => {
    if (itemState === ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID) {
        return {
            title: 'vip_finalPrice',
            amount: finalPrice,
            amountIncludingBuyerProtection: paymentAmountForBid
        };
    }

    if (finalPrice && finalPrice > 0) {
        return {
            title: 'vip_soldFor',
            amount: finalPrice,
            amountIncludingBuyerProtection: paymentAmountForFixedPrice
        };
    }

    if (hasBids) {
        return {
            title: 'vip_leadingBid',
            amount: leadingBid,
            amountIncludingBuyerProtection: paymentAmountForBid
        };
    }
    return {
        title: 'vip_openingBid',
        amount: openingBid,
        amountIncludingBuyerProtection: paymentAmountForBid
    };
};
